import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { fr, enUS } from 'date-fns/locale';
import { 
  Sparkles, 
  Search, 
  MapPin, 
  Calendar, 
  Users, 
  CheckCircle, 
  AlertCircle,
  Globe,
  RefreshCw,
  Import,
  Filter,
  Zap,
  Facebook,
  Instagram
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AIEvents = () => {
  const { t, i18n } = useTranslation();
  const { token } = useAuth();
  const [aiEvents, setAiEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [discovering, setDiscovering] = useState(false);
  const [searchingSocial, setSearchingSocial] = useState(false);
  const [countryFilter, setCountryFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');

  const countries = [
    { value: 'all', label: i18n.language === 'fr' ? 'Tous les pays' : 'All countries' },
    { value: 'FR', label: 'France' },
    { value: 'DE', label: i18n.language === 'fr' ? 'Allemagne' : 'Germany' },
    { value: 'ES', label: i18n.language === 'fr' ? 'Espagne' : 'Spain' },
    { value: 'IT', label: i18n.language === 'fr' ? 'Italie' : 'Italy' },
    { value: 'BE', label: i18n.language === 'fr' ? 'Belgique' : 'Belgium' },
    { value: 'CH', label: i18n.language === 'fr' ? 'Suisse' : 'Switzerland' },
    { value: 'NL', label: i18n.language === 'fr' ? 'Pays-Bas' : 'Netherlands' },
    { value: 'UK', label: i18n.language === 'fr' ? 'Royaume-Uni' : 'United Kingdom' },
  ];

  const eventTypes = [
    { value: 'all', label: i18n.language === 'fr' ? 'Tous types' : 'All types' },
    { value: 'concentration', label: i18n.language === 'fr' ? 'Concentrations' : 'Rallies' },
    { value: 'ride', label: i18n.language === 'fr' ? 'Balades' : 'Rides' },
    { value: 'meeting', label: i18n.language === 'fr' ? 'Rencontres' : 'Meetups' },
    { value: 'race', label: i18n.language === 'fr' ? 'Courses' : 'Races' },
  ];

  useEffect(() => {
    fetchAIEvents();
  }, [countryFilter, typeFilter]);

  const fetchAIEvents = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (countryFilter !== 'all') params.append('country', countryFilter);
      if (typeFilter !== 'all') params.append('event_type', typeFilter);
      
      const response = await axios.get(`${API_URL}/ai/events?${params.toString()}`);
      setAiEvents(response.data);
    } catch (error) {
      console.error('Error fetching AI events:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDiscoverEvents = async () => {
    setDiscovering(true);
    try {
      const params = new URLSearchParams();
      if (countryFilter !== 'all') params.append('country', countryFilter);
      if (typeFilter !== 'all') params.append('event_type', typeFilter);
      
      const response = await axios.post(
        `${API_URL}/ai/discover-events?${params.toString()}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      fetchAIEvents();
    } catch (error) {
      console.error('Error discovering events:', error);
      toast.error(t('common.error'));
    } finally {
      setDiscovering(false);
    }
  };

  const handleSearchSocial = async () => {
    setSearchingSocial(true);
    try {
      const response = await axios.post(
        `${API_URL}/ai/search-social`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(response.data.message);
      fetchAIEvents();
    } catch (error) {
      console.error('Error searching social:', error);
      toast.error(t('common.error'));
    } finally {
      setSearchingSocial(false);
    }
  };

  const handleImportEvent = async (eventId) => {
    try {
      await axios.post(
        `${API_URL}/ai/import-to-map/${eventId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(i18n.language === 'fr' ? 'Événement importé !' : 'Event imported!');
      fetchAIEvents();
    } catch (error) {
      console.error('Error importing event:', error);
      toast.error(t('common.error'));
    }
  };

  const handleVerifyEvent = async (eventId) => {
    try {
      await axios.post(
        `${API_URL}/ai/verify-event/${eventId}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      toast.success(i18n.language === 'fr' ? 'Événement vérifié !' : 'Event verified!');
      fetchAIEvents();
    } catch (error) {
      console.error('Error verifying event:', error);
      toast.error(t('common.error'));
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return format(date, 'PPP', { locale: i18n.language === 'fr' ? fr : enUS });
    } catch {
      return dateStr;
    }
  };

  const getVerificationBadge = (event) => {
    if (event.verification_status === 'verified') {
      return (
        <Badge className="bg-green-500/10 text-green-500 border-green-500/20">
          <CheckCircle className="w-3 h-3 mr-1" />
          {i18n.language === 'fr' ? 'Vérifié' : 'Verified'}
        </Badge>
      );
    }
    return (
      <Badge className="bg-yellow-500/10 text-yellow-500 border-yellow-500/20">
        <AlertCircle className="w-3 h-3 mr-1" />
        {i18n.language === 'fr' ? 'Non vérifié' : 'Unverified'}
      </Badge>
    );
  };

  const getSourceBadge = (event) => {
    if (event.source_type === 'social_media') {
      const platform = event.platform?.toLowerCase();
      if (platform?.includes('facebook')) {
        return <Badge className="bg-blue-600/10 text-blue-400"><Facebook className="w-3 h-3 mr-1" /> Facebook</Badge>;
      }
      if (platform?.includes('instagram')) {
        return <Badge className="bg-pink-500/10 text-pink-400"><Instagram className="w-3 h-3 mr-1" /> Instagram</Badge>;
      }
      return <Badge className="bg-purple-500/10 text-purple-400"><Globe className="w-3 h-3 mr-1" /> Social</Badge>;
    }
    return <Badge className="bg-primary/10 text-primary"><Sparkles className="w-3 h-3 mr-1" /> AI</Badge>;
  };

  return (
    <div className="max-w-7xl mx-auto px-4 md:px-8 py-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-orange-600 flex items-center justify-center">
            <Sparkles className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="font-heading font-bold text-3xl md:text-4xl uppercase tracking-tight">
              {i18n.language === 'fr' ? 'Découverte IA' : 'AI Discovery'}
            </h1>
            <p className="text-muted-foreground">
              {i18n.language === 'fr' 
                ? 'Événements moto vérifiés en Europe, trouvés par GPT-5.2'
                : 'Verified motorcycle events in Europe, found by GPT-5.2'}
            </p>
          </div>
        </div>
      </div>

      {/* Actions */}
      <Card className="mb-8 border-primary/20 bg-gradient-to-r from-primary/5 to-transparent">
        <CardContent className="p-6">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="flex flex-wrap gap-3">
              <Select value={countryFilter} onValueChange={setCountryFilter}>
                <SelectTrigger className="w-[180px]" data-testid="ai-country-filter">
                  <Globe className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {countries.map((c) => (
                    <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={typeFilter} onValueChange={setTypeFilter}>
                <SelectTrigger className="w-[180px]" data-testid="ai-type-filter">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {eventTypes.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="flex gap-3">
              <Button
                onClick={handleSearchSocial}
                disabled={searchingSocial}
                variant="outline"
                className="gap-2"
                data-testid="search-social-btn"
              >
                {searchingSocial ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Facebook className="w-4 h-4" />
                )}
                {i18n.language === 'fr' ? 'Réseaux sociaux' : 'Social Media'}
              </Button>

              <Button
                onClick={handleDiscoverEvents}
                disabled={discovering}
                className="brand-glow gap-2"
                data-testid="discover-events-btn"
              >
                {discovering ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Zap className="w-4 h-4" />
                )}
                {i18n.language === 'fr' ? 'Rechercher événements' : 'Discover Events'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Events Grid */}
      {loading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[1, 2, 3, 4, 5, 6].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardContent className="p-4">
                <div className="h-4 bg-muted rounded w-1/4 mb-2" />
                <div className="h-6 bg-muted rounded w-3/4 mb-4" />
                <div className="h-3 bg-muted rounded w-1/2" />
              </CardContent>
            </Card>
          ))}
        </div>
      ) : aiEvents.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          <AnimatePresence>
            {aiEvents.map((event, index) => (
              <motion.div
                key={event.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -20 }}
                transition={{ delay: index * 0.05 }}
              >
                <Card className="group hover:border-primary/50 transition-colors h-full" data-testid={`ai-event-${event.id}`}>
                  <CardContent className="p-5 flex flex-col h-full">
                    {/* Badges */}
                    <div className="flex flex-wrap gap-2 mb-3">
                      {getSourceBadge(event)}
                      {getVerificationBadge(event)}
                      {event.imported && (
                        <Badge className="bg-green-500/10 text-green-500">
                          <Import className="w-3 h-3 mr-1" />
                          {i18n.language === 'fr' ? 'Importé' : 'Imported'}
                        </Badge>
                      )}
                    </div>

                    {/* Title */}
                    <h3 className="font-heading font-bold text-lg uppercase tracking-tight line-clamp-2 mb-2">
                      {event.title}
                    </h3>

                    {/* Date & Location */}
                    <div className="space-y-2 mb-4 flex-1">
                      <div className="flex items-center gap-2 text-sm text-primary">
                        <Calendar className="w-4 h-4" />
                        <span>{formatDate(event.date)}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <MapPin className="w-4 h-4" />
                        <span className="line-clamp-1">
                          {event.location}{event.country && `, ${event.country}`}
                        </span>
                      </div>
                      {event.source && (
                        <div className="flex items-center gap-2 text-xs text-muted-foreground">
                          <Globe className="w-3 h-3" />
                          <span className="line-clamp-1">{event.source}</span>
                        </div>
                      )}
                    </div>

                    {/* Description */}
                    {event.description && (
                      <p className="text-sm text-muted-foreground line-clamp-2 mb-4">
                        {event.description}
                      </p>
                    )}

                    {/* Confidence Score */}
                    {event.confidence_score > 0 && (
                      <div className="mb-4">
                        <div className="flex items-center justify-between text-xs mb-1">
                          <span className="text-muted-foreground">
                            {i18n.language === 'fr' ? 'Fiabilité' : 'Reliability'}
                          </span>
                          <span className={event.confidence_score >= 70 ? 'text-green-500' : 'text-yellow-500'}>
                            {event.confidence_score}%
                          </span>
                        </div>
                        <div className="h-1.5 bg-muted rounded-full overflow-hidden">
                          <div 
                            className={`h-full rounded-full ${event.confidence_score >= 70 ? 'bg-green-500' : 'bg-yellow-500'}`}
                            style={{ width: `${event.confidence_score}%` }}
                          />
                        </div>
                      </div>
                    )}

                    {/* Actions */}
                    <div className="flex gap-2 mt-auto pt-4 border-t border-border">
                      {!event.imported && (
                        <Button
                          size="sm"
                          onClick={() => handleImportEvent(event.id)}
                          className="flex-1 gap-1"
                          data-testid={`import-event-${event.id}`}
                        >
                          <Import className="w-4 h-4" />
                          {i18n.language === 'fr' ? 'Importer' : 'Import'}
                        </Button>
                      )}
                      {event.verification_status !== 'verified' && (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => handleVerifyEvent(event.id)}
                          className="gap-1"
                          data-testid={`verify-event-${event.id}`}
                        >
                          <CheckCircle className="w-4 h-4" />
                          {i18n.language === 'fr' ? 'Vérifier' : 'Verify'}
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      ) : (
        <Card className="bg-card/50">
          <CardContent className="p-12 text-center">
            <Sparkles className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <h3 className="font-heading font-bold text-xl uppercase">
              {i18n.language === 'fr' ? 'Aucun événement découvert' : 'No events discovered'}
            </h3>
            <p className="text-muted-foreground mt-2 mb-6">
              {i18n.language === 'fr' 
                ? "Utilisez l'IA pour rechercher des événements moto en Europe"
                : 'Use AI to search for motorcycle events across Europe'}
            </p>
            <Button onClick={handleDiscoverEvents} disabled={discovering} className="brand-glow gap-2">
              {discovering ? (
                <RefreshCw className="w-4 h-4 animate-spin" />
              ) : (
                <Zap className="w-4 h-4" />
              )}
              {i18n.language === 'fr' ? 'Lancer la recherche' : 'Start Discovery'}
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AIEvents;
